/* eslint-disable react-hooks/exhaustive-deps */
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import BusinessIcon from "@mui/icons-material/Business";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import PeopleIcon from "@mui/icons-material/People";
import PersonIcon from "@mui/icons-material/Person";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import TablePreloader from "../../../components/Loader/TablePreloader";
import SearchBar from "../../../components/SearchBar";
import Table from "../../../components/table/Table";
import {
  getUserData,
  getUsers,
  setPagination,
} from "../../../features/users/userSlice";
import HomeLayout from "../../../layouts/Home";
import config from "../../../utils/config";
import Menu from "../../../utils/data.dropdown";
import { IError } from "../../../utils/icons.utils";

// User type constants
const USER_TYPES = {
  ALL: "",
  COMPANY_ADMIN: "companyadmin",
  INDIVIDUAL: "individual",
  HMO_ADMIN: "hmoadmin",
  HOSPITAL_ADMIN: "hospitaladmin",
  SUPER_ADMIN: "superadmin",
};

const UserManagement = () => {
  const dispatch = useDispatch();
  const { routes } = config;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const { loading, users, pagination } = useSelector(getUserData);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  useEffect(() => {
    dispatch(getUsers());
  }, [pagination.page, pagination.userType]);

  useEffect(() => {
    let cb = setTimeout(() => (async () => await dispatch(getUsers()))(), 700);

    return () => {
      clearTimeout(cb);
    };
  }, [pagination.search]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const userTypeMap = [
      USER_TYPES.ALL,
      USER_TYPES.COMPANY_ADMIN,
      USER_TYPES.INDIVIDUAL,
      USER_TYPES.HMO_ADMIN,
      USER_TYPES.HOSPITAL_ADMIN,
      USER_TYPES.SUPER_ADMIN,
    ];
    dispatch(setPagination({ userType: userTypeMap[newValue], page: 1 }));
  };

  const data = users?.map((user) => {
    return {
      "First Name": user?.firstName,
      "Last Name": user?.lastName,
      "Email Address": user?.email || "N/A",
      "Phone Number": user?.phoneNumber,
      role: user?.role?.[0] || "N/A",
      _data: user,
    };
  });

  const menu = new Menu({});
  const dropdownMenu = [
    menu.view({
      navigate,
      name:"",
      route: routes.configurations.userManagement,
    }),
  ];

  // Tab configuration with icons and labels
  const tabs = [
    { label: "All Users", icon: <PeopleIcon />, color: "#6366f1" },
    { label: "Company Admins", icon: <BusinessIcon />, color: "#8b5cf6" },
    { label: "Individuals", icon: <PersonIcon />, color: "#10b981" },
    { label: "HMO Admins", icon: <LocalHospitalIcon />, color: "#f59e0b" },
    { label: "Hospital Admins", icon: <LocalHospitalIcon />, color: "#06b6d4" },
    {
      label: "Super Admins",
      icon: <AdminPanelSettingsIcon />,
      color: "#ef4444",
    },
  ];

  return (
    <HomeLayout {...{ title: "User Management" }}>
      {/* Header Section */}
      <Box className="mb-6 hidden">
        <Typography variant="h4" className="font-semibold text-[#040316] mb-2">
          User Management
        </Typography>
        <Typography variant="body2" className="text-gray-600">
          Manage and monitor all users across different roles and organizations
        </Typography>
      </Box>

      {/* Search Bar */}
      <Box className="mb-6">
        <SearchBar
          {...{
            placeholder: "Search for users by name or email",
            onChange: (e) => {
              const { value } = e.target;
              if (e.code === "Enter") dispatch(getUsers(value));
              else dispatch(setPagination({ search: value }));
            },
          }}
        />
      </Box>

      {/* Tabs Section */}
      <Card className="mb-6 shadow-sm">
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 500,
              fontSize: "0.95rem",
              minHeight: "64px",
              color: "#6b7280",
              "&.Mui-selected": {
                color: "var(--c-primary-0)",
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "var(--c-primary-0)",
              height: "3px",
            },
          }}
        >
          {tabs.map((tab, index) => (
            <Tab
              key={index}
              icon={tab.icon}
              iconPosition="start"
              label={
                <Box className="flex items-center gap-2">
                  <span>{tab.label}</span>
                  {index === activeTab && (
                    <Chip
                      label={pagination.total || 0}
                      size="small"
                      sx={{
                        backgroundColor: tab.color,
                        color: "white",
                        fontWeight: 600,
                        height: "20px",
                        fontSize: "0.75rem",
                      }}
                    />
                  )}
                </Box>
              }
            />
          ))}
        </Tabs>
      </Card>

      {/* Table Section */}
      <Card className="shadow-sm">
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <TablePreloader />
          ) : data?.length ? (
            <Box className="p-4">
              <Table
                {...{
                  dropdownMenu,
                  data,
                  pagination,
                  setPagination: (d) => dispatch(setPagination(d())),
                  isLoading: loading,
                }}
              />
            </Box>
          ) : (
            <Box className="pt-12 pb-16 flex flex-col justify-center items-center">
              <img
                className="w-[280px] mb-6 opacity-75"
                src={IError}
                alt="No users"
                loading="lazy"
              />
              <Typography className="text-[400] text-xl text-[#040316] mb-2">
                No users found
              </Typography>
              <Typography className="text-sm text-gray-500 max-w-md text-center">
                {pagination.search
                  ? `No users match your search "${pagination.search}"`
                  : `There are no ${tabs[
                      activeTab
                    ].label.toLowerCase()} in the system at the moment`}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </HomeLayout>
  );
};

export default UserManagement;
